import { ArrowLeft, LogOut } from "lucide-react";
import { Suspense } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { useLogout, useMe } from "@/shared/auth/session";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { LegalLinks } from "@/shared/consent/LegalLinks";
import { PageSkeleton } from "@/shared/components/PageSkeleton";
import { ROUTES } from "@/shared/constants/routes";
import { Button } from "@/shared/ui/button";
import { Icon } from "@/shared/ui/icon";
import { Brand } from "./Brand";
import { ThemeToggle } from "./ThemeToggle";

/**
 * The signed-in-without-an-organisation frame (create organisation, invitations): brand, account,
 * sign out, and a way back into the current organisation when there is one. No sidebar: nothing to navigate yet.
 *
 * The column is wide enough for the plan configurator (M11 §6) to put its summary beside the choices;
 * pages that are a single list keep their own narrower column.
 */
export function OnboardingShell() {
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout(() => void navigate(ROUTES.login, { replace: true }));
  const display = me.user.name || me.user.mobile;

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-fg">
      <header className="flex h-13 shrink-0 items-center gap-3 border-b border-border bg-surface px-4 md:px-6">
        <Brand />
        <div className="ml-auto flex items-center gap-1.5">
          {me.tenant ? (
            <Button asChild variant="ghost" size="sm">
              <Link to={ROUTES.overview}>
                <Icon icon={ArrowLeft} size={16} />
                <span className="hidden sm:inline">Back to {me.tenant.name}</span>
                <span className="sm:hidden">Back</span>
              </Link>
            </Button>
          ) : null}
          <span className="hidden truncate text-cell text-fg-muted md:inline" title={display}>
            {display}
          </span>
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={() => logout.mutate()} loading={logout.isPending}>
            <Icon icon={LogOut} size={16} />
            Sign out
          </Button>
        </div>
      </header>
      <main id="main" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 md:px-6 md:py-12">
        <ErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<PageSkeleton rows={3} />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
      <footer className="mx-auto w-full max-w-5xl px-4 pb-8 md:px-6">
        <LegalLinks className="justify-center" />
      </footer>
    </div>
  );
}
